import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { chatbotApi, workspaceApi, leadsApi, templateApi, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Plus, Loader2, ArrowRight, Bot, PlayCircle, ChevronDown,
  MessageSquare, Users, Calendar, BarChart3, Activity,
} from "lucide-react";
import BicarraVideo, { useSiteLang } from "../components/sections/BicarraVideo";
import { toast } from "sonner";

/* One row of state per assistant: the dot, the words, and the one thing
   to do next. Anything a chatbot can be must land in exactly one of these. */
function describe(status) {
  switch (status) {
    case "live":
      return { tone: "live", label: "Live", note: "Ready to help your visitors", cta: "Open", href: (id) => `/chatbot/${id}` };
    case "training":
      return { tone: "wait", label: "Preparing", note: "Learning your material", cta: "Open", href: (id) => `/chatbot/${id}` };
    case "failed":
      return { tone: "warn", label: "Needs attention", note: "Training did not finish", cta: "Review", href: (id) => `/chatbot/${id}/training` };
    default:
      return { tone: "warn", label: "Needs setup", note: "Finish setup to start answering.", cta: "Continue setup", href: (id) => `/chatbot/${id}/training` };
  }
}

const QUICK_LINKS = [
  { href: "/conversations", label: "Conversations", note: "Read and reply to chats", icon: MessageSquare },
  { href: "/contacts", label: "Contacts", note: "Leads your assistants collected", icon: Users },
  { href: "/appointments", label: "Appointments", note: "Bookings made in chat", icon: Calendar },
  { href: "/insights", label: "Insights", note: "Visitors and trends", icon: BarChart3 },
];

const DOT = {
  live: "bg-b2-livewash text-b2-live",
  wait: "bg-b2-wash text-b2-washink",
  warn: "bg-b2-attentionwash text-b2-attention",
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [chatbots, setChatbots] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [messages, setMessages] = useState(null);
  const [contacts, setContacts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newBot, setNewBot] = useState({ name: "", description: "", template_id: "" });
  const [tutorialOpen, setTutorialOpen] = useState(null); // null = follow default
  const [videoLang] = useSiteLang();

  const firstName = (user?.name || user?.email || "there").split(/[\s@]/)[0];

  useEffect(() => { loadData(); /* eslint-disable-next-line */ }, []);

  const loadData = async () => {
    try {
      const [botsRes, templatesRes, usageRes, leadsRes] = await Promise.all([
        chatbotApi.list(),
        templateApi.list().catch(() => ({ data: [] })),
        workspaceApi.getUsage().catch(() => ({ data: null })),
        leadsApi.list().catch(() => ({ data: null })),
      ]);
      /* Be strict about shape: a proxy or an error page can hand back a
         string where a list is expected, and a blank screen is far worse
         than a missing number. */
      const asList = (v) => (Array.isArray(v) ? v : []);
      const asCount = (v) => (typeof v === "number" ? v : null);
      setChatbots(asList(botsRes?.data));
      setTemplates(asList(templatesRes?.data));
      setMessages(asCount(usageRes?.data?.messages_this_month));
      setContacts(asCount(leadsRes?.data?.total));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newBot.name.trim()) { toast.error("Please give your assistant a name"); return; }
    setCreating(true);
    try {
      const template = newBot.template_id && newBot.template_id !== "blank"
        ? templates.find((t) => t.id === newBot.template_id) : null;
      const res = await chatbotApi.create({
        name: newBot.name,
        description: newBot.description,
        system_prompt: template?.system_prompt || "You are a helpful AI assistant.",
      });
      setCreateOpen(false);
      setNewBot({ name: "", description: "", template_id: "" });
      toast.success("Assistant created");
      navigate(`/chatbot/${res.data.id}/training`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const live = chatbots.filter((c) => c.status === "live").length;
  const needsSetup = chatbots.find((c) => !c.status || c.status === "untrained");

  const stats = [
    { key: "live", label: "Assistants live", value: live, icon: Bot, tone: "bg-b2-livewash text-b2-live",
      note: loading ? "" : `${chatbots.length} assistant${chatbots.length === 1 ? "" : "s"} in total` },
    { key: "messages", label: "Messages this month", value: messages, icon: MessageSquare, tone: "bg-b2-wash text-b2-indigo",
      note: "Handled by your assistants" },
    { key: "contacts", label: "Contacts collected", value: contacts, icon: Users, tone: "bg-b2-attentionwash text-b2-attention",
      note: "From your chat lead forms" },
  ];

  return (
    <div data-testid="home-page">
      {/* BC-022: page head, stat cards, two-column body. */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-[26px] leading-tight font-bold tracking-[-0.02em]">
            Salam, {firstName}
          </h1>
          <p className="mt-1 text-[14px] text-b2-muted">Your assistants, at a glance.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/activity"
            data-testid="view-activity-link"
            className="hidden sm:inline-flex items-center gap-1.5 h-10 px-4 rounded-[8px] border border-b2-linestrong bg-b2-surface text-[14px] font-medium text-b2-inksoft hover:text-b2-indigo hover:border-b2-indigo"
          >
            <Activity className="w-4 h-4" strokeWidth={1.9} /> Recent activity
          </Link>
          <Button
            data-testid="create-bot-btn"
            onClick={() => setCreateOpen(true)}
            className="h-10 px-4 rounded-[8px] bg-b2-indigo hover:bg-b2-indigohover text-white text-[14px] font-semibold shadow-none"
          >
            <Plus className="w-4 h-4 mr-1.5" strokeWidth={2.2} /> Create assistant
          </Button>
        </div>
      </div>

      {/* Only state real numbers. A figure we could not load stays a dash. */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        {stats.map((s) => (
          <div key={s.key} className="b2-card p-5" data-testid={`stat-${s.key}`}>
            <div className="flex items-center justify-between">
              <p className="b2-label">{s.label}</p>
              <span className={`w-9 h-9 rounded-full flex items-center justify-center ${s.tone}`}>
                <s.icon className="w-[18px] h-[18px]" strokeWidth={1.9} />
              </span>
            </div>
            <p className="mt-2 text-[28px] leading-none font-bold tracking-[-0.02em] text-b2-ink tabular-nums">
              {loading || s.value === null || s.value === undefined ? "-" : s.value.toLocaleString()}
            </p>
            <p className="mt-2 text-[13px] text-b2-muted">{s.note}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
        <section className="b2-card xl:col-span-2 overflow-hidden">
          <div className="b2-card-head">
            <div>
              <h2 className="b2-card-title">Your assistants</h2>
              <p className="text-[13px] text-b2-muted">
                {loading ? "Loading" : `${chatbots.length} total, ${live} live`}
              </p>
            </div>
            {chatbots.length > 0 && (
              <button
                type="button" onClick={() => setCreateOpen(true)}
                className="text-[13px] font-semibold text-b2-indigo hover:underline"
              >
                + New
              </button>
            )}
          </div>
          {loading ? (
            <div className="py-20 flex justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-b2-faint" />
            </div>
          ) : chatbots.length === 0 ? (
            <div className="py-16 px-8 text-center">
              <span className="w-12 h-12 mx-auto rounded-full bg-b2-wash text-b2-indigo flex items-center justify-center">
                <Bot className="w-6 h-6" strokeWidth={1.6} />
              </span>
              <p className="mt-4 text-[16px] font-semibold">No assistants yet</p>
              <p className="mt-1.5 text-[14px] text-b2-muted">
                Create one, tell it about your business, and it can start answering.
              </p>
              <Button
                onClick={() => setCreateOpen(true)}
                className="mt-6 h-10 px-4 rounded-[8px] bg-b2-indigo hover:bg-b2-indigohover text-white text-[14px] font-semibold shadow-none"
              >
                <Plus className="w-4 h-4 mr-1.5" strokeWidth={2.2} /> Create assistant
              </Button>
            </div>
          ) : (
            <>
              <div className="hidden sm:grid grid-cols-[minmax(0,1fr)_116px_136px] gap-4 px-5 py-2.5 bg-[#F7F9FC] border-b border-b2-line b2-label">
                <span>Assistant</span><span>Status</span><span className="text-right">Action</span>
              </div>
              <ul className="divide-y divide-b2-line">
                {chatbots.map((bot) => {
                  const s = describe(bot.status);
                  return (
                    <li key={bot.id} className="grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,1fr)_116px_136px] items-center gap-4 px-5 py-4 hover:bg-[#FAFBFD]">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${DOT[s.tone]}`}>
                          <Bot className="w-[18px] h-[18px]" strokeWidth={1.8} />
                        </span>
                        <div className="min-w-0">
                          <p className="text-[14.5px] font-semibold text-b2-ink truncate flex items-center gap-2">
                            <span className="truncate">{bot.name}</span>
                            {/* BC-019/BC-020: this assistant belongs to someone else's workspace.
                                The badge names the role for the three shared roles. */}
                            {["co_owner", "viewer", "agent"].includes(bot.access_role) && (
                              <span
                                title={bot.shared_by ? `Shared by ${bot.shared_by}` : "Shared with you"}
                                className="shrink-0 inline-flex items-center h-5 px-2 rounded-full text-[11px] font-medium bg-b2-wash text-b2-washink"
                                data-testid="shared-badge"
                              >
                                {bot.access_role === "viewer" ? "Shared · Viewer" : bot.access_role === "agent" ? "Shared · Agent" : "Shared"}
                              </span>
                            )}
                          </p>
                          <p className="text-[13px] text-b2-muted truncate">{bot.description || s.note}</p>
                        </div>
                      </div>
                      <span className={`hidden sm:inline-flex w-fit items-center gap-1.5 h-6 px-2.5 rounded-full text-[12px] font-medium ${DOT[s.tone]}`}>
                        <span className="w-1.5 h-1.5 rounded-full bg-current" />
                        {s.label}
                      </span>
                      <Link
                        to={s.href(bot.id)}
                        className="justify-self-end whitespace-nowrap inline-flex items-center gap-1 h-8 px-3 rounded-[8px] border border-b2-line text-[13px] font-medium text-b2-indigo hover:bg-b2-wash hover:border-b2-wash"
                      >
                        {s.cta} <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </section>

        <aside className="grid gap-6 md:grid-cols-2 xl:grid-cols-1 items-start">
          {/* One quiet nudge, only when there is genuinely something to finish. */}
          {!loading && needsSetup && (
            <div data-testid="next-action-panel" className="b2-card p-5 border-l-4 border-l-b2-indigo">
              <p className="b2-label">Next step</p>
              <p className="mt-2 text-[15px] font-semibold text-b2-ink">
                Finish setting up {needsSetup.name}
              </p>
              <p className="mt-1 text-[13.5px] text-b2-muted">
                Add what your assistant should know, then try a conversation.
              </p>
              <Link
                to={`/chatbot/${needsSetup.id}/training`}
                className="mt-4 inline-flex items-center gap-1 text-[14px] font-semibold text-b2-indigo hover:underline"
              >
                Continue setup <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </Link>
            </div>
          )}

          <div className="b2-card overflow-hidden">
            <div className="b2-card-head"><h2 className="b2-card-title">Quick links</h2></div>
            <ul className="divide-y divide-b2-line">
              {QUICK_LINKS.map((q) => (
                <li key={q.href}>
                  <Link to={q.href} className="flex items-center gap-3 px-5 py-3.5 hover:bg-[#FAFBFD] group">
                    <span className="w-9 h-9 rounded-full bg-b2-canvas text-b2-muted group-hover:bg-b2-wash group-hover:text-b2-indigo flex items-center justify-center shrink-0">
                      <q.icon className="w-[17px] h-[17px]" strokeWidth={1.9} />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-[14px] font-semibold text-b2-ink">{q.label}</span>
                      <span className="block text-[12.5px] text-b2-muted truncate">{q.note}</span>
                    </span>
                    <ArrowRight className="w-4 h-4 text-b2-faint group-hover:text-b2-indigo" strokeWidth={1.9} />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>

      {/* Tutorial video: open by default for a brand-new workspace, one tap
          away otherwise. Language follows the interface language setting. */}
      {!loading && (() => {
        const open = tutorialOpen === null ? chatbots.length === 0 : tutorialOpen;
        const T = videoLang === "ms"
          ? { title: "Tutorial video: bina pembantu pertama anda", sub: "Kira-kira 3 minit, langkah demi langkah." }
          : { title: "Video tutorial: build your first assistant", sub: "About 3 minutes, step by step." };
        return (
          <div data-testid="tutorial-panel" className="mt-6 b2-card">
            <button
              type="button"
              onClick={() => setTutorialOpen(!open)}
              aria-expanded={open}
              className="w-full flex items-center gap-4 px-5 py-4 text-left"
            >
              <span className="w-10 h-10 rounded-full bg-b2-wash text-b2-indigo flex items-center justify-center shrink-0">
                <PlayCircle className="w-[19px] h-[19px]" strokeWidth={1.8} />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-[15px] font-semibold text-b2-ink">{T.title}</span>
                <span className="block text-[13px] text-b2-muted">{T.sub}</span>
              </span>
              <ChevronDown className={`w-5 h-5 text-b2-faint transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={1.8} />
            </button>
            {open && (
              <div className="px-5 pb-5">
                <BicarraVideo kind="tutorial" showChapters showSwitch={false} />
              </div>
            )}
          </div>
        );
      })()}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-[14px]">
          <DialogHeader><DialogTitle>Create assistant</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input
                data-testid="bot-name-input"
                value={newBot.name}
                onChange={(e) => setNewBot({ ...newBot, name: e.target.value })}
                placeholder="e.g. Sales Assistant"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea
                data-testid="bot-description-input"
                value={newBot.description}
                onChange={(e) => setNewBot({ ...newBot, description: e.target.value })}
                placeholder="What should this assistant help visitors with?"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Template</Label>
              <Select
                value={newBot.template_id}
                onValueChange={(v) => setNewBot({ ...newBot, template_id: v })}
              >
                <SelectTrigger><SelectValue placeholder="Choose a template (optional)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="blank">Blank</SelectItem>
                  {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button
              data-testid="create-bot-confirm"
              onClick={handleCreate}
              disabled={creating}
              className="w-full h-11 rounded-[10px] bg-b2-indigo hover:bg-b2-indigohover text-white font-semibold shadow-none"
            >
              {creating
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</>
                : "Create assistant"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
